import { captureError } from "./sentry";

/**
 * postgrest-js renvoie ses erreurs en objet littéral `{ message, code, details, hint }`,
 * pas en instance d'`Error`. `String(error)` en faisait « [object Object] » : c'était le
 * titre de l'issue Sentry 147643315, qui mêlait toutes ces erreurs et masquait le code,
 * seul renseignement utile.
 */
const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  if (typeof error === "object" && error !== null) {
    const { message, code } = error as { message?: unknown; code?: unknown };
    if (typeof message === "string" && message.trim().length > 0) {
      const suffix = typeof code === "string" && code.length > 0 && !message.includes(code) ? ` (${code})` : "";
      return new Error(`${message}${suffix}`);
    }
    try {
      return new Error(JSON.stringify(error).slice(0, 300));
    } catch {
      // Structure circulaire : repli ci-dessous.
    }
  }
  return new Error(String(error));
};

/** `code`, `details` et `hint` d'une erreur Supabase, quand ils sont renseignés. */
const supabaseErrorFields = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error || typeof error !== "object" || error === null) return {};
  const { code, details, hint } = error as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries({ code, details, hint }).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );
};

export const reportError = (
  error: unknown,
  context: string,
  extra?: Record<string, unknown>,
) => {
  const err = toError(error);
  if (import.meta.env.DEV) {
    console.error(`[${context}]`, err, extra ?? "");
  }
  void captureError(err, { context, ...supabaseErrorFields(error), ...(extra ?? {}) });
};

export const reportAndSwallow = <T>(
  promise: Promise<T>,
  context: string,
  extra?: Record<string, unknown>,
): Promise<T | void> =>
  promise.catch((e) => {
    reportError(e, context, extra);
  });
